// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// clock.ts — the thing that decides when a tick happens, and it is NOT requestAnimationFrame.
//
// rAF is a render signal, not a clock. A backgrounded tab stops delivering it, so a colony driven by
// rAF freezes the moment the player switches apps and then either jumps or crawls on return; and the
// browser-automation tabs used to verify this game report themselves as `hidden`, so an rAF-driven
// simulation cannot even be OBSERVED advancing in the pass that is supposed to prove it works. Both
// of those are recorded failures in this factory. So: setInterval drives the simulation, rAF (via the
// caller) only paints.
//
// The catch-up is capped at one full second of ticks. Uncapped, a tab that was hidden for ten minutes
// returns and runs six thousand ticks in one frame, which is a lock-up rather than a fast-forward.

import { TICK_MS } from './modes';

export interface Clock {
  /** 0 = paused, 1 = normal, 3 = fast. */
  speed(): number;
  setSpeed(n: number): void;
  toggle(): void;
  stop(): void;
}

const INTERVAL_MS = 33;
const MAX_CATCHUP_TICKS = 1000 / TICK_MS;

export interface ClockOptions {
  /** Advance the simulation exactly one tick. */
  tick(): void;
  /** Called after a batch of ticks, so the caller can repaint once rather than N times. */
  painted(): void;
  /**
   * Called on EVERY interval, including while paused. Anything the interface must keep doing when
   * the simulation is stopped goes here — repainting, and checking whether the current tutorial step
   * has been satisfied. Both of those were once driven by requestAnimationFrame, which is wrong
   * twice over: a paused tutorial step advances no ticks, and rAF does not fire at all in a hidden
   * tab, which is exactly the state the browser used to verify this game reports itself in.
   */
  always?: () => void;
  /** Wall-clock source, injectable so a test never has to wait. */
  now?: () => number;
}

export function createClock(opts: ClockOptions): Clock {
  const now = opts.now ?? (() => Date.now());
  let speed = 1;
  let acc = 0;
  let last = now();

  const handle = setInterval(() => {
    const t = now();
    const dt = t - last;
    last = t;
    opts.always?.();
    if (speed === 0) return;

    acc += dt * speed;
    let n = Math.floor(acc / TICK_MS);
    if (n <= 0) return;
    if (n > MAX_CATCHUP_TICKS) {
      // Drop the backlog rather than spending a whole frame on it.
      n = MAX_CATCHUP_TICKS;
      acc = 0;
    } else {
      acc -= n * TICK_MS;
    }
    for (let i = 0; i < n; i++) opts.tick();
    opts.painted();
  }, INTERVAL_MS);

  return {
    speed: () => speed,
    setSpeed(n: number) {
      speed = n;
      acc = 0;
      last = now();
    },
    toggle() {
      speed = speed === 0 ? 1 : 0;
      acc = 0;
      last = now();
    },
    stop() {
      clearInterval(handle);
    },
  };
}
